import { Building2, Users, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_ABOUT_IMAGE = "https://images.unsplash.com/photo-1603406620746-61b559c90138?w=800&q=80";

const AboutSection = ({ content, images }) => {
  const aboutImage = images && images.length > 0 
    ? `${BACKEND_URL}${images[0].url}` 
    : DEFAULT_ABOUT_IMAGE;

  const extra = content?.extra_fields || {};

  const defaultFeatures = [
    { title: 'Institution régionale', description: 'Association à but non lucratif au service du territoire' },
    { title: "Réseau d'acteurs", description: 'Décideurs économiques, finance, droit et institutions' },
    { title: 'Soutien institutionnel', description: 'Appuyé par la Région BFC et la CCIR' }
  ];

  const features = extra.features || defaultFeatures;
  const featureIcons = [Building2, Users, Award];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-[#D4AF37]/10 text-[#D4AF37] text-sm font-inter font-medium tracking-wide uppercase rounded-full mb-4">
            {extra.badge_text || 'À propos'}
          </span>
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
            {content?.title || 'Qui sommes-nous ?'}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#1e3a8a] to-[#D4AF37] mx-auto mb-6" />
          <p className="text-lg text-slate-600 font-inter max-w-2xl mx-auto">
            {content?.subtitle || 'Une association au service du territoire'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image Side */}
          <div className="relative">
            <div className="relative">
              <img
                src={aboutImage}
                alt="Paysage Bourgogne-Franche-Comté"
                className="w-full h-[400px] object-cover shadow-xl"
                data-testid="about-image"
              />
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 border-4 border-[#D4AF37] -z-10" />
            </div>
            
            {/* Stats card */}
            <div className="absolute -bottom-8 -left-4 lg:left-8 bg-[#1e3a8a] text-white p-6 shadow-xl">
              <p className="text-sm font-inter uppercase tracking-wide text-white/70 mb-1">
                {extra.network_badge_line1 || 'Membre du'}
              </p>
              <p className="font-playfair font-semibold text-lg">
                {extra.network_badge_line2 || 'Réseau des Places'}
              </p>
              <p className="font-playfair font-semibold text-lg">
                {extra.network_badge_line3 || 'Financières de France'}
              </p>
            </div>
          </div>

          {/* Content Side */}
          <div>
            <div className="prose prose-lg max-w-none">
              <p className="text-slate-600 font-inter leading-relaxed mb-6">
                {content?.content || "La Place Financière est une association qui regroupe des acteurs de la finance, du chiffre et du droit, des décideurs économiques, des entreprises, ainsi que les représentants des institutions, CCI, Région et syndicats professionnels."}
              </p>
            </div>

            {/* Missions */}
            {extra.missions && extra.missions.length > 0 && (
              <div className="mt-6">
                <h3 className="font-playfair text-xl font-semibold text-[#0F172A] mb-4">
                  {extra.missions_title || 'Notre objet'}
                </h3>
                <ul className="space-y-3">
                  {extra.missions.map((mission, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-slate-600 font-inter leading-relaxed">{mission}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Governance */}
            {content?.items && content.items.length > 0 && (
              <div className="mt-8 pt-8 border-t border-slate-200">
                <h3 className="font-playfair text-xl font-semibold text-[#0F172A] mb-4">
                  {extra.governance_title || 'Gouvernance'}
                </h3>
                <div className="space-y-4">
                  {content.items.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#1e3a8a]/5 rounded flex items-center justify-center flex-shrink-0">
                        <span className="font-playfair font-bold text-[#1e3a8a]">{item.value?.charAt(0) || 'P'}</span>
                      </div>
                      <div>
                        <p className="font-inter font-medium text-[#0F172A]">{item.value}</p>
                        <p className="text-sm text-slate-500 font-inter">{item.label} — {item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20">
          {features.map((feature, idx) => {
            const IconComponent = featureIcons[idx] || Building2;
            return (
              <div
                key={idx}
                className="text-center p-8 bg-slate-50 hover:bg-white hover:shadow-xl transition-all duration-300 group"
                data-testid={`about-feature-${idx}`}
              >
                <div className="w-16 h-16 bg-[#1e3a8a]/5 group-hover:bg-[#1e3a8a] rounded-full flex items-center justify-center mx-auto mb-4 transition-colors duration-300">
                  <IconComponent className="w-8 h-8 text-[#1e3a8a] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-playfair text-lg font-semibold text-[#0F172A] mb-2">{feature.title}</h3>
                <p className="text-slate-600 font-inter text-sm">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
